import { useId } from "react";
import { directoryCategories } from "@vimos/api-client";

/**
 * Selector de categoria del Directorio. Las categorias son controladas por
 * Vimos (no se crean desde aqui): es un catalogo fijo con categorias generales y
 * subcategorias opcionales. El valor es el id estable de la categoria (o "").
 */
export interface CategorySelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export function CategorySelect({
  label,
  value,
  onChange,
  error,
  hint,
  disabled,
  id,
  className = "",
}: CategorySelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const hintId = hint ? `${selectId}-hint` : undefined;
  const errorId = error ? `${selectId}-error` : undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={selectId} className="font-body text-ink text-sm font-medium">
        {label}
      </label>
      <select
        id={selectId}
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`border-sand/70 bg-paper text-ink focus-visible:ring-gold focus-visible:ring-offset-paper h-11 rounded-md border px-3 text-base transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${error ? "border-red-500" : "hover:border-ink/25"}`}
      >
        <option value="">Selecciona una categoria</option>
        {directoryCategories.map((category) =>
          category.children.length > 0 ? (
            <optgroup key={category.id} label={category.label}>
              <option value={category.id}>{category.label} (general)</option>
              {category.children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.label}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={category.id} value={category.id}>
              {category.label}
            </option>
          ),
        )}
      </select>
      {hint && !error ? (
        <p id={hintId} className="text-mist text-sm">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
